import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  List,
  ListItem,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon,
  Article as ArticleIcon,
  Twitter as TwitterIcon,
} from '@mui/icons-material';
import companyLogo from '../../assets/landing/logo/company-logo-2.png';

const Footer = () => {
  return (
    <Box
      sx={{
        bgcolor: '#174473',
        color: 'white',
        pt: 6,
        pb: 2,
      }}
    >
      <Container sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Logo and Address Section */}
          <Grid item md={5} xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Link
                href="https://asqi.in/"
                sx={{ display: 'block', mb: 2 }}
                target="_blank"
              >
                <img
                  src={companyLogo}
                  alt="ASQI Logo"
                  width={110}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </Link>
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                }}
              >
                ADVANCING SUSTAINABILITY, QUALITY AND IMPACT
              </Typography>
              <Box sx={{ display: 'flex', mt: 1 }}>
                <LocationOnIcon sx={{ mr: 1, fontSize: 18, mt: 0.3 }} />
                <Link
                  href="https://maps.app.goo.gl/Co49e1NbPy4aTKTt6"
                  color="inherit"
                  underline="none"
                  sx={{
                    fontSize: '0.9rem',
                  }}
                  target="_blank"
                >
                  H 532, Kanakia Zillion, BKC Annexe, Kurla (West), Mumbai
                  400070, INDIA
                </Link>
              </Box>
            </Box>
          </Grid>

          {/* Navigation Links */}
          <Grid item md={2} xs={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <List dense disablePadding>
                {['Home', 'About Us', 'Our Team', 'Blogs'].map(text => (
                  <ListItem
                    key={text}
                    disablePadding
                    sx={{
                      justifyContent: 'flex-start',
                      py: 0.5,
                    }}
                  >
                    <Link
                      href={
                        text === 'Home'
                          ? '/'
                          : `https://asqi.in/${text
                              .toLowerCase()
                              .replace(' ', '-')}`
                      }
                      target={text === 'Home' ? '_self' : '_blank'}
                      color="inherit"
                      underline="none"
                      sx={{
                        transition: 'color 0.3s ease-in-out',
                        '&:hover': {
                          color: '#0DDB06',
                        },
                      }}
                    >
                      {text}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item md={2} xs={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <Link
                    href="tel:+919372186859"
                    color="inherit"
                    underline="none"
                    sx={{
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                        color: '#0DDB06',
                      },
                    }}
                  >
                    +91 9372186859
                  </Link>
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <Link
                    href="mailto:enquiry@asqi.in"
                    color="inherit"
                    underline="none"
                    sx={{
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                        color: '#0DDB06',
                      },
                    }}
                  >
                    enquiry@asqi.in
                  </Link>
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <Link
                    href="https://asqi.in/contact-us/"
                    color="inherit"
                    underline="none"
                    sx={{
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                        color: '#0DDB06',
                      },
                    }}
                    target="_blank"
                  >
                    Contact Us
                  </Link>
                </ListItem>
              </List>

              {/* Social Media Icons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mt: 2,
                  justifyContent: 'flex-start',
                }}
              >
                <IconButton
                  href="https://www.linkedin.com/company/asqi/"
                  target="_blank"
                  sx={{
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    padding: '6px',
                  }}
                  size="small"
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
                <IconButton
                  href="https://asqi.medium.com/"
                  target="_blank"
                  sx={{
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    padding: '6px',
                  }}
                  size="small"
                >
                  <ArticleIcon fontSize="small" />
                </IconButton>
                <IconButton
                  href="https://x.com/ASQI_Tech"
                  target="_blank"
                  sx={{
                    color: 'white',
                    border: '1px solid white',
                    borderRadius: '4px',
                    padding: '6px',
                  }}
                  size="small"
                >
                  <TwitterIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* Company Info */}
          <Grid item md={3} xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ color: 'black', p: 1 }}>
                    <strong>ASQI Advisors Pvt Ltd</strong> <br />
                    ASQI Advisors is the parent holding entity of ASQI
                    Investment Managers, responsible for corporate governance
                    and strategic oversight of its subsidiaries.
                  </Typography>
                }
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'window',
                      },
                    },
                  ],
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: 2,
                      border: '1px solid rgba(0, 0, 0, 0.2)', // Optional: Adds a subtle border for clarity
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'white',
                    },
                  },
                }}
              >
                <Link
                  href="https://asqi.in/about-us/"
                  color="inherit"
                  underline="none"
                  sx={{ display: { xs: 'none', md: 'block' }, mb: 1.5 }}
                  target="_blank"
                >
                  Details of Holding Company &gt;
                </Link>
              </Tooltip>

              <Tooltip
                title={
                  <Typography variant="body2" sx={{ color: 'black', p: 1 }}>
                    ASQI Advisors Pvt. Ltd.
                  </Typography>
                }
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'window',
                      },
                    },
                  ],
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: 2,
                      border: '1px solid rgba(0, 0, 0, 0.2)', // Optional: Adds a subtle border for clarity
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'white',
                    },
                  },
                }}
              >
                <Link
                  href="#"
                  color="inherit"
                  underline="none"
                  sx={{ display: { xs: 'none', md: 'block' }, mb: 1.5 }}
                >
                  ASQI Group Entities &gt;
                </Link>
              </Tooltip>

              <Link
                href="https://asqi.in/policies/"
                color="inherit"
                underline="none"
                sx={{
                  display: 'block',
                  mb: 1.5,
                  fontWeight: 'bold',
                }}
                target="_blank"
              >
                Company Policies
              </Link>

              <Typography variant="body2" sx={{ textAlign: 'left' }}>
                <strong>CIN</strong> U62099MH2016FTC274805
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Copyright */}
      <Typography variant="body2" align="center" sx={{}}>
        Copyright © {new Date().getFullYear()} ASQI | All Rights Reserved.
      </Typography>
    </Box>
  );
};

export default Footer;
